import { useEffect, useMemo, useState } from "react";
import { KeyRound, TimerReset } from "lucide-react";
import { StaffCard } from "@/components/schedora/StaffCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp, useOwnerSnapshot } from "@/context/AppContext";
import { supabase } from "@/lib/supabase";

interface ClockEventRow {
  id: string;
  created_at: string;
  event_type: "clock_in" | "clock_out";
  staff_member_id: string;
}

export default function OwnerClockIn() {
  const { currentBusiness, clockStaff } = useApp();
  const snapshot = useOwnerSnapshot(new Date().toISOString().slice(0, 10));
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [events, setEvents] = useState<ClockEventRow[]>([]);

  const loadEvents = async () => {
    if (!currentBusiness) return;

    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    const { data, error: nextError } = await supabase
      .from("staff_clock_events")
      .select("id, created_at, event_type, staff_member_id")
      .eq("business_id", currentBusiness.id)
      .gte("created_at", startOfDay.toISOString())
      .order("created_at", { ascending: false })
      .limit(20);

    if (nextError) {
      setError(nextError.message);
      return;
    }

    setEvents((data ?? []) as ClockEventRow[]);
  };

  useEffect(() => {
    void loadEvents();
  }, [currentBusiness?.id]);

  const selectedStaff = snapshot?.staff.find((entry) => entry.id === selectedStaffId) ?? null;

  const timeline = useMemo(() => {
    if (!snapshot) return [];

    return events.map((event) => {
      const staffMember = snapshot.staff.find((entry) => entry.id === event.staff_member_id);
      return {
        ...event,
        staffName: staffMember?.name ?? "Unknown staff",
      };
    });
  }, [events, snapshot]);

  const submit = async () => {
    setError("");
    setMessage("");

    if (!selectedStaffId) {
      setError("Choose a staff member first.");
      return;
    }

    if (!/^\d{4}$/.test(pin)) {
      setError("Enter a 4-digit PIN.");
      return;
    }

    setIsSubmitting(true);
    const result = await clockStaff(selectedStaffId, pin);
    setIsSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setMessage(result.message);
    setPin("");
    await loadEvents();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Staff Clock In</h1>
          <p className="text-sm text-muted-foreground">Choose a team member, enter their PIN, and clock them in or out from one shared page.</p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3 text-sm text-muted-foreground">
          {snapshot?.staff.filter((entry) => entry.isClockedIn).length ?? 0} clocked in right now
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.6fr_0.9fr]">
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {snapshot?.staff.map((member) => (
              <StaffCard
                key={member.id}
                {...member}
                selectable
                selected={member.id === selectedStaffId}
                onSelect={() => {
                  setSelectedStaffId(member.id);
                  setError("");
                  setMessage("");
                }}
              />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 text-foreground">
              <KeyRound className="h-4 w-4" />
              <h2 className="font-semibold">Clock In Kiosk</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {selectedStaff ? `Selected: ${selectedStaff.name}` : "Select a staff member to continue."}
            </p>

            <div className="mt-4 space-y-3">
              <Input
                type="password"
                inputMode="numeric"
                placeholder="Enter 4-digit PIN"
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 4))}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              {message && <p className="text-sm text-emerald-600">{message}</p>}
              <Button className="w-full" onClick={() => void submit()} disabled={isSubmitting}>
                {isSubmitting ? "Checking PIN..." : selectedStaff?.isClockedIn ? "Clock Out" : "Clock In"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-2 text-foreground">
              <TimerReset className="h-4 w-4" />
              <h2 className="font-semibold">Today’s Activity</h2>
            </div>
            <div className="mt-4 space-y-3">
              {timeline.length === 0 && (
                <p className="text-sm text-muted-foreground">No clock events yet today.</p>
              )}
              {timeline.map((event) => (
                <div key={event.id} className="rounded-xl border border-border/70 px-3 py-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-foreground">{event.staffName}</span>
                    <span className="text-muted-foreground">
                      {new Date(event.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    {event.event_type === "clock_in" ? "Clocked in" : "Clocked out"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
